import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { type BuilderPage, type PageType } from '@/types/schema/BuilderPage';
import { type PageEditorSelectedItem } from '@/pages/pages/page-editor/helpers/types';

type PagesPageContextState = {
  isSettingsPanelOpen: boolean;
  setIsSettingsPanelOpen: (isOpen: boolean) => void;
  settingsPanelItem: PagesPageSettingsPanelItem | null;
  setSettingsPanelItem: (item: PagesPageSettingsPanelItem | null) => void;
  isAddNewPageDialogOpen: boolean;
  setIsAddNewPageDialogOpen: (isOpen: boolean) => void;
  newPageType: PageType;
  setNewPageType: (type: PageType) => void;
} | null;

type PagesPageContextProviderProps = {
  children: React.ReactNode;
};

export type PagesPageSettingsPanelItem =
  | {
      type: 'page';
      page: BuilderPage;
    }
  | PageEditorSelectedItem;

const PagesPageContext = createContext<PagesPageContextState>(null);

export function PagesPageContextProvider({ children }: PagesPageContextProviderProps) {
  const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
  const [settingsPanelItem, setSettingsPanelItem] = useState<PagesPageSettingsPanelItem | null>(
    null
  );
  const [newPageType, setNewPageType] = useState<PageType>('page');
  const [isAddNewPageDialogOpen, setIsAddNewPageDialogOpen] = useState(false);

  const handleSetSettingsPanelItem = useCallback((item: PagesPageSettingsPanelItem | null) => {
    setSettingsPanelItem(item);

    // We want to ensure the settings panel is open when setting an item
    setIsSettingsPanelOpen(true);
  }, []);

  const contextValue = useMemo(
    () => ({
      isSettingsPanelOpen,
      setIsSettingsPanelOpen,
      settingsPanelItem,
      setSettingsPanelItem: handleSetSettingsPanelItem,
      isAddNewPageDialogOpen,
      setIsAddNewPageDialogOpen,
      newPageType,
      setNewPageType
    }),
    [
      handleSetSettingsPanelItem,
      isSettingsPanelOpen,
      settingsPanelItem,
      isAddNewPageDialogOpen,
      newPageType
    ]
  );

  return <PagesPageContext.Provider value={contextValue}>{children}</PagesPageContext.Provider>;
}

export const usePagesPageContext = () => {
  const context = useContext(PagesPageContext);
  if (!context) {
    throw new Error('usePagesPageContext must be used within a PagesPageContextProvider');
  }
  return context;
};
